import React, { useRef, useEffect } from "react";
import PropTypes from "prop-types";
import { useNavigate, Navigate } from "react-router-dom";
import { useAuth } from "react-oidc-context";
import { useAuthorization } from "../../auth/useAuthorization";
import { PERMISSIONS } from "../../auth/permissions";
import Header from "../Header/Header";
import Footer from "../Footer/Footer";
import WizardProgressHeader from "../WizardProgressHeader/WizardProgressHeader";
import "./AuthenticatedLayout.scss";

function AuthenticatedLayout({ children }) {
  const auth = useAuth();
  const navigate = useNavigate();
  const hasRedirected = useRef(false);
  const { hasPermission } = useAuthorization();

  useEffect(() => {
    if (
      !auth.isAuthenticated &&
      !auth.isLoading &&
      !auth.activeNavigator &&
      !auth.error &&
      !hasRedirected.current
    ) {
      hasRedirected.current = true;
      navigate("/login", { replace: true });
    }
  }, [auth.isAuthenticated, auth.isLoading, auth.activeNavigator, auth.error, navigate]);

  const handleSignOut = async () => {
    await auth.removeUser();
    await auth.signoutRedirect({
      post_logout_redirect_uri: window.location.origin,
    });
  };

  if (auth.isLoading) {
    return (
      <div className="al-loading">
        <div className="al-spinner" />
        <p>Loading…</p>
      </div>
    );
  }

  if (auth.error) {
    return (
      <div className="al-error">
        <p>Authentication error: {auth.error.message}</p>
        <button
          onClick={() => {
            sessionStorage.clear();
            auth.signinRedirect();
          }}
        >
          Retry Sign In
        </button>
      </div>
    );
  }

  if (!auth.isAuthenticated) {
    return (
      <div className="al-loading">
        <p>Redirecting to sign in…</p>
      </div>
    );
  }

  if (!hasPermission(PERMISSIONS.ACCESS_APPLICATION)) {
    return <Navigate to="/access-denied" replace />;
  }

  const email = auth.user?.profile?.email || auth.user?.profile?.sub || "User";

  return (
    <div className="al-shell">
      <Header email={email} onSignOut={handleSignOut} />
      <WizardProgressHeader />
      <main className="al-main">{children}</main>
      <Footer />
    </div>
  );
}

AuthenticatedLayout.propTypes = {
  children: PropTypes.node,
};

export default AuthenticatedLayout;
